import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChartBarIcon,
  ArrowsUpDownIcon,
  ArrowTrendingUpIcon,
  BeakerIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { ReactComponent as Logo } from '../../logo.svg';
import { ReactComponent as LogoIcon } from '../../logo-icon.svg';

export interface AppSidebarProps {
  collapsed: boolean;
  canToggle: boolean;
  onToggle: () => void;
  converterState: 'loading' | 'enabled' | 'gated';
  onConverterContact: () => void;
  showConverterDemo: boolean;
  /** Usage is for saved instances only */
  showUsage: boolean;
  /** Instance name for the profile/settings item; null hides the item */
  instanceName: string | null;
  onHelp: () => void;
  onOpenLegal: (type: 'terms' | 'privacy') => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed,
  canToggle,
  onToggle,
  converterState,
  onConverterContact,
  showConverterDemo,
  showUsage,
  instanceName,
  onHelp,
  onOpenLegal,
}) => {
  const location = useLocation();

  // Width animates over 100ms. Swap the header (wide logo, toggle button) only
  // after it finishes, so they never render in a narrow sidebar.
  const [headerWide, setHeaderWide] = React.useState(!collapsed);
  React.useEffect(() => {
    if (collapsed) {
      setHeaderWide(false);
      return;
    }
    const t = setTimeout(() => setHeaderWide(true), 100);
    return () => clearTimeout(t);
  }, [collapsed]);

  // True once the width settles. Hide the toggle button while it animates.
  const settled = headerWide === !collapsed;

  const itemClass = (active: boolean) =>
    `${active ? 'nav-link-active' : 'btn-subtle'} w-full ${collapsed ? 'justify-center px-0' : ''}`;

  const label = (text: string) =>
    collapsed ? null : <span className="truncate min-w-0">{text}</span>;

  const analyzerActive = location.pathname === '/' || location.pathname.startsWith('/jobs/');
  const converterActive =
    location.pathname.startsWith('/converter') && location.pathname !== '/converter-demo';

  return (
    <aside
      className={`${collapsed ? 'w-16' : 'w-60'} shrink-0 overflow-hidden bg-widget-bg border-r border-secondary flex flex-col sticky top-0 h-screen transition-[width] duration-100`}
    >
      <div className={`h-16 shrink-0 flex items-center border-b border-secondary ${headerWide ? 'justify-between px-4' : 'justify-center px-2'}`}>
        <Link to="/" className="flex items-center" aria-label="Alchemist home">
          {headerWide ? (
            <Logo className="h-6 w-auto" style={{ color: '#F8F9FA' }} />
          ) : (
            <LogoIcon className="h-6 w-auto" style={{ color: '#F8F9FA' }} />
          )}
        </Link>
        {headerWide && settled && canToggle && (
          <button
            onClick={onToggle}
            className="btn-icon text-body-color hover:bg-body-bg/50"
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
          >
            <ChevronDoubleLeftIcon className="w-5 h-5" />
          </button>
        )}
      </div>
      {!headerWide && settled && canToggle && (
        <div className="px-2 pt-2 shrink-0">
          <button
            onClick={onToggle}
            className="btn-subtle w-full justify-center px-0"
            title="Expand sidebar"
            aria-label="Expand sidebar"
          >
            <ChevronDoubleRightIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      <nav className={`flex-1 min-h-0 overflow-y-auto py-4 space-y-1 ${collapsed ? 'px-2' : 'px-3'}`}>
        <Link
          to="/"
          className={itemClass(analyzerActive)}
          title={collapsed ? 'Analyzer' : undefined}
        >
          <ChartBarIcon className="w-5 h-5 shrink-0" />
          {label('Analyzer')}
        </Link>

        {converterState === 'loading' ? (
          <button
            disabled
            className={`${itemClass(false)} opacity-60 cursor-not-allowed`}
            title={collapsed ? 'Converter' : undefined}
          >
            <ArrowsUpDownIcon className="w-5 h-5 shrink-0" />
            {label('Converter')}
          </button>
        ) : converterState === 'enabled' ? (
          <Link
            to="/converter"
            className={itemClass(converterActive)}
            title={collapsed ? 'Converter' : undefined}
          >
            <ArrowsUpDownIcon className="w-5 h-5 shrink-0" />
            {label('Converter')}
          </Link>
        ) : (
          <button
            onClick={onConverterContact}
            className={itemClass(false)}
            title={collapsed ? 'Converter' : undefined}
          >
            <ArrowsUpDownIcon className="w-5 h-5 shrink-0" />
            {label('Converter')}
          </button>
        )}

        {showUsage && (
          <Link
            to="/usage"
            className={itemClass(location.pathname.startsWith('/usage'))}
            title={collapsed ? 'Usage' : undefined}
          >
            <ArrowTrendingUpIcon className="w-5 h-5 shrink-0" />
            {label('Usage')}
          </Link>
        )}

        {showConverterDemo && (
          <Link
            to="/converter-demo"
            className={itemClass(location.pathname === '/converter-demo')}
            title={collapsed ? 'Converter Demo' : undefined}
          >
            <BeakerIcon className="w-5 h-5 shrink-0" />
            {label('Converter Demo')}
          </Link>
        )}
      </nav>

      {/* Pinned bottom: profile/settings, help, portal, then branding/legal */}
      <div className={`shrink-0 border-t border-secondary py-2 space-y-1 ${collapsed ? 'px-2' : 'px-3'}`}>
        {instanceName !== null && (
          <Link
            to="/settings"
            className={itemClass(location.pathname === '/settings')}
            title={collapsed ? `${instanceName || 'Instance'} — Settings` : 'Instance settings'}
          >
            <UserCircleIcon className="w-5 h-5 shrink-0" />
            {label(instanceName || 'Instance')}
          </Link>
        )}
        <button onClick={onHelp} className={itemClass(false)} title={collapsed ? 'Help' : undefined}>
          <QuestionMarkCircleIcon className="w-5 h-5 shrink-0" />
          {label('Help')}
        </button>
        <a
          href="https://app.getalchemist.io/"
          target="_blank"
          rel="noopener noreferrer"
          className={itemClass(false)}
          title={collapsed ? 'Portal' : undefined}
        >
          <ArrowTopRightOnSquareIcon className="w-5 h-5 shrink-0" />
          {label('Portal')}
        </a>
      </div>

      {!collapsed && (
        <div className="shrink-0 border-t border-secondary/50 mx-3 pt-3 pb-3 px-1 text-xs text-body-color/60 text-center">
          <p>
            Made by{' '}
            <a
              href="https://t1a.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-hover transition-colors"
              style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 800 }}
            >
              T1A
            </a>
          </p>
          <div className="flex items-center justify-center gap-2 mt-1">
            <button onClick={() => onOpenLegal('privacy')} className="hover:text-primary transition-colors">
              Privacy Policy
            </button>
            <span className="text-body-color/30">|</span>
            <button onClick={() => onOpenLegal('terms')} className="hover:text-primary transition-colors">
              Terms of Use
            </button>
          </div>
        </div>
      )}
    </aside>
  );
};

export default AppSidebar;
